// Fælles HUD-skin for Digitaliserings scene-nære feedback: tool-instruktionen (_showInstruction), koordinat-boksen,
// kopi-feedback (toast) og hover-tooltip. HUD er en SÆRSKILT, mørk familie (PointViews overlay-kontrakt, frontendens
// VIEWER-UI-OVERLAYS.md afsnit 1.4/1.8): mørk, halvgennemsigtig flade, hvid tekst, radius 4 - fordi den ligger direkte over
// punktskyen (en lys flade ville være ulæselig dér). Potree ejer den; ingen Vue.
//
// Læser frontendens --pv-hud-*-tokens med fallback-værdier, så standalone Potree (uden Vue-CSS) ser ens ud. Injiceres én gang.
// Funktion og lifecycle er uændret hos hver komponent - kun udseende og viewport-placering er fælles.
const HUD_STYLE_ID = 'pvh-style';
const HUD_CSS = `
.pvh{box-sizing:border-box;background:var(--pv-hud-bg,rgba(20,20,20,0.88));color:var(--pv-hud-text,#fff);
 border-radius:var(--pv-hud-radius,4px);border:1px solid rgba(255,255,255,0.12);box-shadow:0 1px 4px rgba(0,0,0,0.35);
 font-family:Roboto,Arial,sans-serif;font-size:12px;line-height:1.35;text-shadow:0 1px 2px rgba(0,0,0,0.6);overflow-wrap:anywhere}
.pvh--top{position:fixed;transform:translateX(-50%);width:max-content;text-align:center;pointer-events:none;padding:4px 10px}
.pvh--tooltip{position:fixed;pointer-events:none;padding:3px 8px;white-space:nowrap;z-index:var(--pv-z-tooltip,2147483500)}
.pvh-status{background:none;border:none;box-shadow:none;padding:0;text-align:left;width:min(520px,100%);display:flex;flex-direction:column;gap:5px}
.pvh-status-rows{display:flex;flex-direction:column;gap:5px}
.pvh-status .potree_message,.pvh-status-row,.pvh-status-chip{pointer-events:auto}
.pvh-status-row{display:flex;align-items:flex-start;gap:8px;padding:5px 8px;box-sizing:border-box;background:var(--pv-hud-bg,rgba(20,20,20,0.88));color:var(--pv-hud-text,#fff);
 border:1px solid rgba(255,255,255,0.12);border-left:3px solid #64b5f6;border-radius:var(--pv-hud-radius,4px);box-shadow:0 1px 4px rgba(0,0,0,0.35)}
.pvh-status-row--success{border-left-color:#81c784}
.pvh-status-row--warning{border-left-color:#ffb74d}
.pvh-status-row--error{border-left-color:#ef5350;background:var(--pv-hud-error-bg,rgba(183,28,28,0.92))}
.pvh-status-text{flex:1 1 auto;min-width:0}
.pvh-status-detail{display:block;opacity:0.8;font-size:11px}
.pvh-status-icon{flex:0 0 auto;width:14px;text-align:center}
.pvh-status-btn{background:none;border:1px solid rgba(255,255,255,0.35);color:inherit;border-radius:3px;font:inherit;padding:1px 6px;cursor:pointer;flex:0 0 auto}
.pvh-status-chip{align-self:flex-end;background:var(--pv-hud-bg,rgba(20,20,20,0.88))}
.pvh-status-x{background:none;border:none;color:inherit;opacity:0.8;cursor:pointer;font:inherit;font-size:14px;line-height:1;flex:0 0 auto;padding:0 2px}
.pvh-spin{flex:0 0 auto;width:12px;height:12px;margin:2px;box-sizing:border-box;border:2px solid rgba(255,255,255,0.35);border-top-color:#fff;border-radius:50%;animation:pvh-spin 1s linear infinite}
@keyframes pvh-spin{to{transform:rotate(360deg)}}
.pvh-status-bar{height:2px;margin-top:3px;background:rgba(255,255,255,0.25)}
.pvh-status-bar>i{display:block;height:100%;background:#fff}
`;

export function ensureHudStyle(){
	if(document.getElementById(HUD_STYLE_ID)) return;
	const style = document.createElement('style');
	style.id = HUD_STYLE_ID;
	style.textContent = HUD_CSS;
	document.head.appendChild(style);
}

// Det synlige viewer-område (viewer-containerens rect, skåret til viewporten) - topbar/sidebar forskyder det, så HUD placeres
// ud fra det og aldrig ud fra window (0,0). Uden en viewer-container (fx test) bruges hele viewporten.
function viewerArea(){
	const el = document.getElementById('potree_render_area');
	const r = el ? el.getBoundingClientRect() : null;
	const left = r ? Math.max(r.left, 0) : 0;
	const top = r ? Math.max(r.top, 0) : 0;
	const width = Math.max(0, (r ? Math.min(r.right, window.innerWidth) : window.innerWidth) - left);
	return {left, top, width};
}

function hudTopOffset(){
	const raw = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--pv-hud-top-offset'));
	return isFinite(raw) && raw >= 0 ? raw : 10;
}

// Top-centreret HUD-element: centreret over viewer-området (ikke over vinduet), `topOffset` under områdets top, max-bredde
// begrænset til området (lange tekster ombrydes frem for at klippes). `below`: et andet (synligt) HUD-element, som dette
// placeres lige under, så to samtidige aldrig overlapper, selv når det øverste ombrydes til flere linjer.
// `topOffset` (standard: CSS-variablen --pv-hud-top-offset, ellers 10 px): værten kan holde HUD fri af sin egen overlay-toolbar, som
// ligger øverst i viewer-området (PointView sætter den til toolbarens højde + margen).
export function placeHudTopCenter(el, {topOffset = hudTopOffset(), below = null, gap = 6, maxWidth = 520, margin = 10} = {}){
	const area = viewerArea();
	el.style.left = `${Math.round(area.left + area.width / 2)}px`;
	el.style.maxWidth = `${Math.max(120, Math.min(maxWidth, area.width - 2 * margin))}px`;
	let top = area.top + topOffset;
	if(below && below.isConnected && below.style.display !== 'none'){
		top = below.getBoundingClientRect().bottom + gap;
	}
	el.style.top = `${Math.round(top)}px`;
}

// HUD-stakken: de øverste pladser i viewer-området står i fast rækkefølge - instruktion, kort bekræftelse, status - og hver synlig plads
// placeres lige under den foregående (som kan ombrydes til flere linjer), så de aldrig overlapper hinanden, uanset i hvilken rækkefølge
// de dukker op. Hver ejer (instruktion i DigitizationModule, toast i CopyFeedback, status i HudStatus) melder sit element og kalder
// layoutHudStack(), når det vises, skjules eller skifter tekst. Hændelsesdrevet; ingen kobling til renderloopet.
const HUD_SLOT_ORDER = ['instruction', 'toast', 'status'];
const hudSlots = {instruction: new Set(), toast: new Set(), status: new Set()};

export function registerHudSlot(slot, el){
	if(hudSlots[slot]) hudSlots[slot].add(el);
}

export function unregisterHudSlot(slot, el){
	if(hudSlots[slot]) hudSlots[slot].delete(el);
}

export function layoutHudStack(){
	let previous = null;
	for(const slot of HUD_SLOT_ORDER){
		for(const el of hudSlots[slot]){
			if(!el.isConnected || el.style.display === 'none') continue; // (flere hint-elementer kan findes, hvis modulet er instantieret flere gange)
			placeHudTopCenter(el, {below: previous});
			previous = el;
		}
	}
}
